import { useEffect, useRef, useCallback } from 'react';
import styles from './NoizeGraf.module.css';

export const NoizeGraf = ({
  noiseLevel = 40,
  maxNoiseLevel: maxLevel = 120,
  variant = 'full',
}) => {
  const canvasRef = useRef(null);
  const rafIdRef = useRef(null);
  const prevNoiseLevelRef = useRef(noiseLevel);

  // Функция для рисования скругленных прямоугольников
  const roundRect = useCallback((ctx, x, y, width, height, radius) => {
    if (height < 2 * radius) radius = height / 2;
    if (width < 2 * radius) radius = width / 2;

    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + width, y, x + width, y + radius, radius);
    ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
    ctx.arcTo(x, y + height, x, y + height - radius, radius);
    ctx.arcTo(x, y, x + radius, y, radius);
    ctx.closePath();
  }, []);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const totalBars = 35;
    const barWidth = width / totalBars;
    const barSpacing = 8;
    const borderRadius = 0;

    ctx.clearRect(0, 0, width, height);

    const targetLevel = Math.min(Math.max(noiseLevel, 0), maxLevel);

    let grayBarsLeft = 0;
    switch (variant) {
      case 'leftGap':
        grayBarsLeft = 2;
        break;
      case 'leftGapLarge':
        grayBarsLeft = 3;
        break;
      default:
        grayBarsLeft = 0;
    }

    const activeBars = totalBars - grayBarsLeft;

    const globalGradient = ctx.createLinearGradient(0, 0, width, 0);
    globalGradient.addColorStop(0, '#ffefff');
    globalGradient.addColorStop(0.2, '#ffb1ff');
    globalGradient.addColorStop(0.4, '#ff1fff');
    globalGradient.addColorStop(0.6, '#ea24ff');
    globalGradient.addColorStop(0.8, '#672bff');
    globalGradient.addColorStop(1, '#1636ff');

    for (let i = 0; i < totalBars; i++) {
      const x = i * barWidth;
      const actualBarWidth = barWidth - barSpacing;

      ctx.fillStyle = 'rgba(151, 146, 151, 0.2)';
      roundRect(ctx, x, 0, actualBarWidth, height, borderRadius);
      ctx.fill();
    }

    if (activeBars > 0) {
      for (let i = grayBarsLeft; i < totalBars; i++) {
        const x = i * barWidth;
        const actualBarWidth = barWidth - barSpacing;

        const activeIndex = i - grayBarsLeft;
        const barNumber = activeBars - activeIndex;

        const barThreshold = (barNumber / activeBars) * maxLevel;
        const prevBarThreshold = ((barNumber - 1) / activeBars) * maxLevel;

        let fillPercentage = 0;

        if (targetLevel >= barThreshold) {
          fillPercentage = 100;
        } else if (targetLevel > prevBarThreshold) {
          const barRange = barThreshold - prevBarThreshold;
          const levelInBar = targetLevel - prevBarThreshold;
          fillPercentage = (levelInBar / barRange) * 100;
        }

        if (fillPercentage > 0) {
          const fillWidth = actualBarWidth * (fillPercentage / 100);

          ctx.save();
          roundRect(ctx, x, 0, actualBarWidth, height, borderRadius);
          ctx.clip();
          ctx.fillStyle = globalGradient;
          ctx.fillRect(x + (actualBarWidth - fillWidth), 0, fillWidth, height);
          ctx.restore();
        }
      }
    }
  }, [noiseLevel, maxLevel, variant, roundRect]);

  useEffect(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      drawCanvas();
      prevNoiseLevelRef.current = noiseLevel;
    });

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [drawCanvas, noiseLevel]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleResize = () => {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(drawCanvas);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(canvas);
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [drawCanvas]);

  return <canvas ref={canvasRef} className={styles.noizeCanvas} />;
};
